import { useLabourHour } from "@hooks/crud/useLabourHours";
import { useSocialBusiness } from "@hooks/crud/useSocialBusiness";
import { useSocialProcurement } from "@hooks/crud/useSocialProcurement";
import { ReportAnalysisLineItem } from "@interfaces/analysis";
import {
  SocialCommitmentTypeHour,
  SocialCommitmentTypeSpend,
} from "@interfaces/commitment";
import {
  EmployeeSocialProcurementFactor,
  EmploymentType,
  Gender,
} from "@interfaces/employee";
import { LabourHourLineItem } from "@interfaces/labourHour";
import { SocialBusinessLineItem } from "@interfaces/socialBusiness";
import { SocialProcurementLineItem } from "@interfaces/socialProcurement";
import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DirectoryCard } from "@stories/atoms/DirectoryCard/DirectoryCard";
import { Loader } from "@stories/atoms/Loaders/Loader/Loader";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { SubTitle } from "@stories/atoms/SubTitle/SubTitle";

interface PackageToolsProps {
  loading: boolean;
  reportDate: Date;
  reportData: ReportAnalysisLineItem[];
  socialProcurementType: SocialCommitmentTypeHour | SocialCommitmentTypeSpend;
}

interface LabourHoursTableRow {
  month: number;
  year: number;
  amount: number;
  contractorReportId: string;
  contractorEmployeeId: string;
  employeeName: string;
  localHoursAchieved: number;
  localHoursPrev: number;
  mpsgHoursAchieved: number;
  mpsgHoursPrev: number;
  femaleHoursAchieved: number;
  femaleHoursPrev: number;
  indigHoursAchieved: number;
  indigHoursPrev: number;
  disAdvHoursAchieved: number;
  disAdvHoursPrev: number;
  socialFactors: SocialCommitmentTypeHour[];
}

const createDefaultLabourHoursTableRow = (
  labourHour: LabourHourLineItem,
  month: number,
  year: number
): LabourHoursTableRow => {
  return {
    month,
    year,
    amount: labourHour.amount,
    contractorReportId: labourHour.reportId,
    contractorEmployeeId: labourHour.employeeId,
    socialFactors: [],
    employeeName: labourHour.employeeName,
    localHoursAchieved: 0,
    localHoursPrev: 0,
    mpsgHoursAchieved: 0,
    mpsgHoursPrev: 0,
    femaleHoursAchieved: 0,
    femaleHoursPrev: 0,
    indigHoursAchieved: 0,
    indigHoursPrev: 0,
    disAdvHoursAchieved: 0,
    disAdvHoursPrev: 0,
  };
};

interface SocialProcurementTableRow {
  month: number;
  year: number;
  invoiceValue: number;
  supplier: string;
  copyOfInvoice: string;
  transactionDate: Date;
  socialFactors: string[];
}

const createDefaultSocialProcurementTableRow = (
  socialProcurement: SocialProcurementLineItem,
  month: number,
  year: number
): SocialProcurementTableRow => {
  return {
    month,
    year,
    invoiceValue: socialProcurement.invoiceValue,
    supplier: socialProcurement.supplier,
    transactionDate: socialProcurement.transactionDate,
    socialFactors: socialProcurement.socialFactors,
    copyOfInvoice: socialProcurement.copyOfInvoice,
  };
};

interface LabourHoursTableProps {
  loading: boolean;
  month: number;
  year: number;
  labourHours: LabourHourLineItem[];
  reportData: ReportAnalysisLineItem[];
  socialFactor: SocialCommitmentTypeHour | SocialCommitmentTypeSpend;
}
const LabourHoursTable = ({
  loading,
  month,
  year,
  labourHours,
  reportData,
  socialFactor,
}: LabourHoursTableProps) => {
  // export enum SocialCommitmentTypeHour {
  //   LocalJobsFirst = "social-commitment-type_local-jobs-first",
  //   MPSGHoursRequirement = "social-commitment-mpsg-hours-requirment",
  //   OpportunitiesForDisadvantagedVictorians = "social-commitment-type_opportunities-for-disadvantaged-victorians",
  //   FemaleParticipation = "social-commitment-type_female-participation",
  //   AboriginalTorresStraitIslanderParticipation = "social-commitment-type_aboriginal-torres-strait-island-participation",
  // }

  // export enum EmployeeSocialProcurementFactor {
  //   LocalJobsFirst = "employee-social-procurement-factor_local-jobs-first",
  //   AborignialOrTorresStraitIslander = "employee-social-procurement-factor_aborignial-or-torres-strait-islander",
  //   AsylumSeekerRefugee = "employee-social-procurement-factor_asylum-seeker-refugee",
  //   DisengagedYouth = "employee-social-procurement-factor_disengaged-youth",
  //   LongTermUnemployed = "employee-social-procurement-factor_long-term-unemployed",
  //   PersonWithDisability = "employee-social-procurement-factor_person-with-disability",
  //   Veteran = "employee-social-procurement-factor_verteran",
  //   CulturallyAndLinguisticallyDiverse = "employee-social-procurement-factor_culterually-and-linguistically-diverse",
  //   PersonsAffectedByFamilyViolence = "employee-social-procurement-factor_person-affected-by-family-violence",
  // }

  const getSocialFactor = (labhourHourRow: LabourHoursTableRow) => {
    switch (socialFactor) {
      default:
      case SocialCommitmentTypeHour.LocalJobsFirst:
        return labhourHourRow.localHoursAchieved;
      case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
        return labhourHourRow.indigHoursAchieved;
      case SocialCommitmentTypeHour.FemaleParticipation:
        return labhourHourRow.femaleHoursAchieved;
      case SocialCommitmentTypeHour.MPSGHoursRequirement:
        return labhourHourRow.mpsgHoursAchieved;
      case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
        return labhourHourRow.disAdvHoursAchieved;
    }
  };

  const allReportRows = [] as LabourHoursTableRow[];
  for (let i = 0; i < labourHours.length; i++) {
    const labourHour = labourHours[i];

    const reportDate = reportData.find(
      (r) => r.reportDateMonth === month && r.reportDateYear === year
    );
    if (reportDate) {
      let labourHourRow = createDefaultLabourHoursTableRow(
        labourHour,
        month,
        year
      );
      for (let i = 0; i < reportData.length - 1; i++) {
        const report = reportData[i];
        if (report.reportDateMonth === month && report.reportDateYear) {
          break;
        }
        const hourData = report.hourData.filter(
          (h) => h.contractorEmployeeId === labourHour.employeeId
        );

        labourHourRow = hourData.reduce((acc: LabourHoursTableRow, e) => {
          return {
            ...acc,
          };
        }, labourHourRow);
      }

      allReportRows.push(labourHourRow);
    }
  }

  // export enum EmployeeSocialProcurementFactor {
  //   LocalJobsFirst = "employee-social-procurement-factor_local-jobs-first",
  //   AborignialOrTorresStraitIslander = "employee-social-procurement-factor_aborignial-or-torres-strait-islander",
  //   AsylumSeekerRefugee = "employee-social-procurement-factor_asylum-seeker-refugee",
  //   DisengagedYouth = "employee-social-procurement-factor_disengaged-youth",
  //   LongTermUnemployed = "employee-social-procurement-factor_long-term-unemployed",
  //   PersonWithDisability = "employee-social-procurement-factor_person-with-disability",
  //   Veteran = "employee-social-procurement-factor_verteran",
  //   CulturallyAndLinguisticallyDiverse = "employee-social-procurement-factor_culterually-and-linguistically-diverse",
  //   PersonsAffectedByFamilyViolence = "employee-social-procurement-factor_person-affected-by-family-violence",
  // }

  const getDisplayValue = (labhourHourRow: LabourHoursTableRow) => {
    switch (socialFactor) {
      default:
      case SocialCommitmentTypeHour.LocalJobsFirst:
        return labhourHourRow.localHoursAchieved;
      case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
        return labhourHourRow.indigHoursAchieved;
      case SocialCommitmentTypeHour.FemaleParticipation:
        return labhourHourRow.femaleHoursAchieved;
      case SocialCommitmentTypeHour.MPSGHoursRequirement:
        return labhourHourRow.mpsgHoursAchieved;
      case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
        return labhourHourRow.disAdvHoursAchieved;
    }
  };
  const getDisplayPrevValue = (labhourHourRow: LabourHoursTableRow) => {
    switch (socialFactor) {
      default:
      case SocialCommitmentTypeHour.LocalJobsFirst:
        return labhourHourRow.localHoursPrev;
      case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
        return labhourHourRow.indigHoursPrev;
      case SocialCommitmentTypeHour.FemaleParticipation:
        return labhourHourRow.femaleHoursPrev;
      case SocialCommitmentTypeHour.MPSGHoursRequirement:
        return labhourHourRow.mpsgHoursPrev;
      case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
        return labhourHourRow.disAdvHoursPrev;
    }
  };
  return (
    <StyledCard>
      <Grid container spacing={2} sx={{ padding: "12px" }}>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            alignContent: "Center",
            justifyContent: "center",
            margin: "auto",
            padding: "12px",
          }}
          justifyContent={"center"}
        >
          <SubTitle title={"Labour Hours"} />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee Name</TableCell>
                <TableCell>Previous Hours</TableCell>
                <TableCell>Current Hours</TableCell>
                <TableCell>Total Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                allReportRows.map((lh, index) => {
                  return (
                    <TableRow key={`${index}-labour-hours-table-row`}>
                      <TableCell>{`${lh.employeeName}`}</TableCell>
                      <TableCell>{`${lh.amount} Hours`}</TableCell>
                      <TableCell>{`${getDisplayPrevValue(
                        lh
                      )} Hours`}</TableCell>
                      <TableCell>{`${getDisplayValue(lh)} Hours`}</TableCell>
                      <TableCell>{`${getDisplayValue(lh)} Hours`}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
            {!loading && allReportRows.length === 0 ? (
              <>
                <TableRow key={"no-data-row-0"}>
                  <TableCell colSpan={4} align="center"></TableCell>
                </TableRow>
                <TableRow key={"no-data-row-1"}>
                  <TableCell colSpan={4} align="center">
                    <Typography variant={"h4"} component="div" mt={1}>
                      No hours found
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow key={"no-data-row-2"}>
                  <TableCell colSpan={4} align="center"></TableCell>
                </TableRow>
              </>
            ) : null}
            {loading ? <Loader /> : null}
          </Table>
        </Grid>
      </Grid>
    </StyledCard>
  );
};

interface SocialProcurementTableProps {
  loading: boolean;
  month: number;
  year: number;
  socialProcurements: SocialProcurementLineItem[];
  reportData: ReportAnalysisLineItem[];
  socialFactor: SocialCommitmentTypeHour | SocialCommitmentTypeSpend;
}
const SocialProcurementTable = ({
  loading,
  month,
  year,
  socialProcurements,
  reportData,
  socialFactor,
}: SocialProcurementTableProps) => {
  const allReportRows = [] as SocialProcurementTableRow[];
  for (let i = 0; i < socialProcurements.length; i++) {
    const socialProcurement = socialProcurements[i];
    const reportDate = reportData.find(
      (r) => r.reportDateMonth === month && r.reportDateYear === year
    );
    if (reportDate) {
      const socialProcurementRow = createDefaultSocialProcurementTableRow(
        socialProcurement,
        month,
        year
      );
      allReportRows.push(socialProcurementRow);
    }
  }

  return (
    <StyledCard>
      <Grid container spacing={2} sx={{ padding: "12px" }}>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            alignContent: "Center",
            justifyContent: "center",
            margin: "auto",
            padding: "12px",
          }}
          justifyContent={"center"}
        >
          <SubTitle title={"Social Spend"} />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Supplier</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Transaction Date</TableCell>
                <TableCell>Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                allReportRows.map((sp, index) => (
                  <TableRow key={`${index}-social-spend-table-row`}>
                    <TableCell>{`${sp.supplier}`}</TableCell>
                    <TableCell>{`$${sp.invoiceValue}`}</TableCell>
                    <TableCell>{`${sp.transactionDate.toLocaleString()}`}</TableCell>
                    <TableCell>
                      <Button>Download</Button>
                    </TableCell>
                  </TableRow>
                ))}
              {!loading && allReportRows.length === 0 && !loading ? (
                <>
                  <TableRow key={"no-data-row-0"}>
                    <TableCell colSpan={4} align="center"></TableCell>
                  </TableRow>
                  <TableRow key={"no-data-row-1"}>
                    <TableCell colSpan={4} align="center">
                      <Typography variant={"h4"} component="div" mt={1}>
                        No Spend found
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow key={"no-data-row-2"}>
                    <TableCell colSpan={4} align="center"></TableCell>
                  </TableRow>
                </>
              ) : null}
            </TableBody>
            {loading ? <Loader /> : null}
          </Table>
        </Grid>
      </Grid>
    </StyledCard>
  );
};

interface CompanyProcurementTableProps {
  month: number;
  year: number;
  loading: boolean;
  socialBusinesses: SocialBusinessLineItem[];
}
const CompanyProcurementTable = ({
  socialBusinesses,
  loading,
}: CompanyProcurementTableProps) => {
  return (
    <>
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              alignContent: "Center",
              justifyContent: "center",
              margin: "auto",
              padding: "12px",
            }}
            justifyContent={"center"}
          >
            <SubTitle title={"Social Business Directory"} />
            <Table>
              <TableHead>
                <TableCell>Matching Businesses</TableCell>
              </TableHead>
              {!loading &&
                socialBusinesses.map((c) => (
                  <DirectoryCard
                    name={c.socialBusinessName}
                    logo={c.socialBusinessLogo}
                    descriptions={c.socialBusinessBlurb}
                    benefits={c.socialBusinessBeneficiaries}
                    url={c.socialBusinessWebsite}
                  />
                ))}

              {socialBusinesses.length === 0 && !loading ? (
                <TableBody>
                  <TableRow key={"no-data-row-0"}>
                    <TableCell colSpan={4} align="center"></TableCell>
                  </TableRow>
                  <TableRow key={"no-data-row-1"}>
                    <TableCell colSpan={4} align="center">
                      <Typography variant={"h4"} component="div" mt={1}>
                        No companies found
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow key={"no-data-row-2"}>
                    <TableCell colSpan={4} align="center"></TableCell>
                  </TableRow>
                </TableBody>
              ) : null}
              {loading ? <Loader /> : null}
            </Table>
          </Grid>
        </Grid>
      </StyledCard>
    </>
  );
};

export const PackageTools = ({
  loading,
  reportDate,
  reportData,
  socialProcurementType,
}: PackageToolsProps) => {
  const month = reportDate.getMonth();
  const year = reportDate.getFullYear();
  const report = reportData.find(
    (r) => r.reportDateMonth === month && r.reportDateYear === year
  );
  //List of labour hours for category
  const { labourHours, isLabourHourLoading } = useLabourHour(report?.reportId);
  const { socialProcurements, isSocialProcurementLoading } =
    useSocialProcurement(report?.reportId);
  const { socialBusinesses, isSocialBusinessLoading } = useSocialBusiness(
    socialProcurementType
  );

  const listComponent = (() => {
    switch (socialProcurementType) {
      case SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation:
      case SocialCommitmentTypeHour.FemaleParticipation:
      case SocialCommitmentTypeHour.MPSGHoursRequirement:
      case SocialCommitmentTypeHour.OpportunitiesForDisadvantagedVictorians:
      case SocialCommitmentTypeHour.LocalJobsFirst:
        return (
          <LabourHoursTable
            loading={isLabourHourLoading}
            month={month}
            year={year}
            socialFactor={socialProcurementType}
            reportData={reportData}
            labourHours={labourHours}
          />
        );
      case SocialCommitmentTypeSpend.LocalContent:
      case SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend:
      case SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend:
        return (
          <SocialProcurementTable
            loading={isSocialProcurementLoading}
            month={month}
            year={year}
            socialFactor={socialProcurementType}
            socialProcurements={socialProcurements.filter((s) =>
              s.socialFactors.includes(socialProcurementType)
            )}
            reportData={reportData}
          />
        );
    }
  })();

  return (
    <Stack direction={"row"}>
      <Box sx={{ minWidth: "50%", minHeight: "25%" }}>{listComponent}</Box>
      <Box sx={{ width: "12px", minWidth: "12px" }} />
      <Box sx={{ minWidth: "50%", minHeight: "25%" }}>
        <CompanyProcurementTable
          month={month}
          year={year}
          loading={isSocialBusinessLoading}
          socialBusinesses={socialBusinesses}
        />
      </Box>
    </Stack>
  );
};
